import { useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import { supabase } from '@lib/supabase';
import Button from '@components/Button';

type Feature = {
  id: string;
  key: string;
  title: string;
  description?: string | null;
  active: boolean;
  created_at: string | null;
};

type Badge = {
  id: string;
  name: string;
  score?: number | null;
};

type BadgeFeatureMapping = {
  badge_id: string;
  feature_id: string;
  created_at: string | null;
  badge?: Badge;
  feature?: Feature;
};

type RawBadgeFeatureMapping = {
  badge_id: string;
  feature_id: string;
  created_at: string | null;
  badge?: Badge | Badge[] | null;
  feature?: Feature | Feature[] | null;
};

type SortKey = 'title' | 'key' | 'active' | 'created_at';

type BadgeSortKey = 'badge' | 'feature' | 'created_at';

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function normalizeBadgeFeatureRows(
  rows: RawBadgeFeatureMapping[] | null | undefined
): BadgeFeatureMapping[] {
  if (!rows) return [];
  return rows.map((row) => ({
    badge_id: row.badge_id,
    feature_id: row.feature_id,
    created_at: row.created_at,
    badge: Array.isArray(row.badge) ? row.badge[0] : (row.badge ?? undefined),
    feature: Array.isArray(row.feature)
      ? row.feature[0]
      : (row.feature ?? undefined),
  }));
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminFeatures() {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureSearch, setFeatureSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [badges, setBadges] = useState<Badge[]>([]);
  const [badgeFeatures, setBadgeFeatures] = useState<BadgeFeatureMapping[]>([]);
  const [activeTab, setActiveTab] = useState<'features' | 'badge_features'>(
    'features'
  );
  const [badgeFeatureSearch, setBadgeFeatureSearch] = useState('');
  const [debouncedBadgeFeatureSearch, setDebouncedBadgeFeatureSearch] =
    useState('');
  const [sort, setSort] = useState<{
    key: SortKey | null;
    dir: 'asc' | 'desc' | null;
  }>({ key: 'created_at', dir: 'desc' });

  const [badgeSort, setBadgeSort] = useState<{
    key: BadgeSortKey | null;
    dir: 'asc' | 'desc' | null;
  }>({ key: 'created_at', dir: 'desc' });
  const [stats, setStats] = useState<{
    total: number | null;
    active: number | null;
    inactive: number | null;
  }>({ total: null, active: null, inactive: null });

  const [editing, setEditing] = useState<Feature | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState<Feature | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    title: string;
    key: string;
    description: string | null;
    active: boolean;
  }>({ title: '', key: '', description: null, active: true });

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: qErr } = await supabase
          .from('features')
          .select('*')
          .order('created_at', { ascending: false });
        if (qErr) throw qErr;
        if (isMounted) {
          const rows = (data ?? []) as Feature[];
          setFeatures(rows);
          const total = rows.length;
          const activeCount = rows.filter((r) => r.active).length;
          setStats({
            total,
            active: activeCount,
            inactive: total - activeCount,
          });
        }
      } catch (err) {
        if (isMounted)
          setError(
            err instanceof Error
              ? err.message
              : String(err ?? 'Unable to load features')
          );
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const badgesResp = await supabase
          .from('badges')
          .select('id,name,score')
          .order('score', { ascending: false })
          .order('name', { ascending: true });
        const mappingsResp = await supabase
          .from('badge_features')
          .select(
            'badge_id,feature_id,created_at,badge:badges(id,name,score),feature:features(*)'
          )
          .order('created_at', { ascending: false });
        if (!isMounted) return;
        setBadges((badgesResp.data ?? []) as Badge[]);
        setBadgeFeatures(
          normalizeBadgeFeatureRows(
            (mappingsResp.data ?? null) as RawBadgeFeatureMapping[] | null
          )
        );
      } catch (err) {
        console.warn('Failed to load badge_features or badges', err);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(featureSearch.trim().toLowerCase()),
      300
    );
    return () => clearTimeout(t);
  }, [featureSearch]);

  useEffect(() => {
    const t = setTimeout(
      () =>
        setDebouncedBadgeFeatureSearch(badgeFeatureSearch.trim().toLowerCase()),
      300
    );
    return () => clearTimeout(t);
  }, [badgeFeatureSearch]);

  const filtered = useMemo(() => {
    if (!debouncedSearch) return features;
    return features.filter((f) =>
      `${f.title} ${f.key} ${f.description ?? ''}`
        .toLowerCase()
        .includes(debouncedSearch)
    );
  }, [features, debouncedSearch]);

  const badgeFeatureFiltered = useMemo(() => {
    if (!debouncedBadgeFeatureSearch) return badgeFeatures;
    return badgeFeatures.filter((m) => {
      const hay =
        `${m.badge?.name ?? ''} ${m.feature?.title ?? ''} ${m.feature?.key ?? ''}`.toLowerCase();
      return hay.includes(debouncedBadgeFeatureSearch);
    });
  }, [badgeFeatures, debouncedBadgeFeatureSearch]);

  const badgeRows = useMemo(() => {
    if (!badgeSort.key || !badgeSort.dir) return badgeFeatureFiltered;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const copy = [...badgeFeatureFiltered];
    copy.sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (badgeSort.key) {
        case 'badge':
          va = a.badge?.name ?? a.badge_id;
          vb = b.badge?.name ?? b.badge_id;
          break;
        case 'feature':
          va = a.feature?.title ?? a.feature_id;
          vb = b.feature?.title ?? b.feature_id;
          break;
        case 'created_at':
        default:
          va = a.created_at ? new Date(a.created_at).getTime() : 0;
          vb = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
      }
      let result = 0;
      if (typeof va === 'number' && typeof vb === 'number') result = va - vb;
      else result = collator.compare(String(va), String(vb));
      return badgeSort.dir === 'asc' ? result : -result;
    });
    return copy;
  }, [badgeFeatureFiltered, badgeSort]);

  const rows = useMemo(() => {
    if (!sort.key || !sort.dir) return filtered;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const copy = [...filtered];
    copy.sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (sort.key) {
        case 'title':
          va = a.title;
          vb = b.title;
          break;
        case 'key':
          va = a.key;
          vb = b.key;
          break;
        case 'active':
          va = a.active ? 1 : 0;
          vb = b.active ? 1 : 0;
          break;
        case 'created_at':
        default:
          va = a.created_at ? new Date(a.created_at).getTime() : 0;
          vb = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
      }
      let result = 0;
      if (typeof va === 'number' && typeof vb === 'number') result = va - vb;
      else result = collator.compare(String(va), String(vb));
      return sort.dir === 'asc' ? result : -result;
    });
    return copy;
  }, [filtered, sort]);

  const badgeFeatureHasData = badgeRows
    ? badgeRows.length > 0
    : badgeFeatureFiltered.length > 0;

  function toggleSort(key: SortKey) {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  }

  function toggleBadgeSort(key: BadgeSortKey) {
    setBadgeSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  }

  const openAdd = () => {
    setEditing(null);
    setFormError(null);
    setFormData({ title: '', key: '', description: null, active: true });
    setIsModalOpen(true);
  };

  const handleEdit = (f: Feature) => {
    setEditing(f);
    setFormError(null);
    setFormData({
      title: f.title,
      key: f.key,
      description: f.description ?? null,
      active: f.active,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditing(null);
  };

  const openDelete = (f: Feature) => {
    setDeleting(f);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDelete = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setDeleting(null);
    setDeleteError(null);
  };

  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [mappingFormError, setMappingFormError] = useState<string | null>(null);
  const [mappingFormData, setMappingFormData] = useState<{
    badge_id: string;
    feature_id: string;
  }>({ badge_id: '', feature_id: '' });
  const [mappingSaving, setMappingSaving] = useState(false);
  const [mappingEditing, setMappingEditing] =
    useState<BadgeFeatureMapping | null>(null);

  const openAddMapping = () => {
    setMappingEditing(null);
    setMappingFormError(null);
    setMappingFormData({
      badge_id: badges[0]?.id ?? '',
      feature_id: features[0]?.id ?? '',
    });
    setIsMappingModalOpen(true);
  };

  const openEditMapping = (m: BadgeFeatureMapping) => {
    setMappingEditing(m);
    setMappingFormError(null);
    setMappingFormData({ badge_id: m.badge_id, feature_id: m.feature_id });
    setIsMappingModalOpen(true);
  };

  const closeMappingModal = () => {
    if (mappingSaving) return;
    setIsMappingModalOpen(false);
    setMappingEditing(null);
  };

  const handleMappingInputChange = (
    field: keyof typeof mappingFormData,
    value: string
  ) => {
    setMappingFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveMapping = async (e?: FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    setMappingSaving(true);
    setMappingFormError(null);
    try {
      if (!mappingFormData.badge_id || !mappingFormData.feature_id)
        throw new Error('Both badge and feature are required');
      const payload = {
        badge_id: mappingFormData.badge_id,
        feature_id: mappingFormData.feature_id,
        created_at: new Date().toISOString(),
      };
      if (!mappingEditing) {
        const { data, error } = await supabase
          .from('badge_features')
          .insert(payload)
          .select(
            'badge_id,feature_id,created_at,badge:badges(id,name,score),feature:features(*)'
          );
        if (error) throw error;
        const added = normalizeBadgeFeatureRows(
          (data ?? null) as RawBadgeFeatureMapping[] | null
        );
        setBadgeFeatures((prev) => [...added, ...prev]);
        setIsMappingModalOpen(false);
      } else {
        // editing an existing mapping: if nothing changed, just close
        if (
          mappingEditing.badge_id === mappingFormData.badge_id &&
          mappingEditing.feature_id === mappingFormData.feature_id
        ) {
          setIsMappingModalOpen(false);
          setMappingEditing(null);
        } else {
          // insert new mapping then try to delete old one
          const { data: insertData, error: insertErr } = await supabase
            .from('badge_features')
            .insert(payload)
            .select(
              'badge_id,feature_id,created_at,badge:badges(id,name,score),feature:features(*)'
            );
          if (insertErr) throw insertErr;
          const added = normalizeBadgeFeatureRows(
            (insertData ?? null) as RawBadgeFeatureMapping[] | null
          );

          try {
            const { error: delErr } = await supabase
              .from('badge_features')
              .delete()
              .match({
                badge_id: mappingEditing.badge_id,
                feature_id: mappingEditing.feature_id,
              });
            if (delErr) console.warn('Failed to delete old mapping', delErr);
          } catch (delErr) {
            console.warn('Failed to delete old mapping', delErr);
          }

          setBadgeFeatures((prev) => {
            // remove old mapping then add new
            const filtered = prev.filter(
              (it) =>
                !(
                  it.badge_id === mappingEditing.badge_id &&
                  it.feature_id === mappingEditing.feature_id
                )
            );
            return [...added, ...filtered];
          });
          setIsMappingModalOpen(false);
          setMappingEditing(null);
        }
      }
    } catch (err) {
      setMappingFormError(
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to save mapping')
      );
    } finally {
      setMappingSaving(false);
    }
  };

  const handleDeleteMapping = async (m: BadgeFeatureMapping) => {
    try {
      const { error } = await supabase
        .from('badge_features')
        .delete()
        .match({ badge_id: m.badge_id, feature_id: m.feature_id });
      if (error) throw error;
      setBadgeFeatures((prev) =>
        prev.filter(
          (it) =>
            !(it.badge_id === m.badge_id && it.feature_id === m.feature_id)
        )
      );
    } catch (err) {
      console.warn('Failed to delete mapping', err);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const { error } = await supabase
        .from('features')
        .delete()
        .eq('id', deleting.id);
      if (error) throw error;
      setFeatures((prev) => prev.filter((p) => p.id !== deleting.id));
      setStats((prev) => {
        const total = (prev.total ?? 0) - 1;
        const active = (prev.active ?? 0) - (deleting.active ? 1 : 0);
        return { total, active, inactive: total - active };
      });
      setShowDeleteModal(false);
      setDeleting(null);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : String(err ?? 'Failed to delete')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleInputChange = (
    field: keyof typeof formData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        const updates = {
          title: formData.title.trim(),
          key: formData.key.trim(),
          description: formData.description?.trim() || null,
          active: formData.active,
        };
        const { data: updatedRows, error: updateError } = await supabase
          .from('features')
          .update(updates)
          .eq('id', editing.id)
          .select('*')
          .maybeSingle();
        if (updateError) throw updateError;
        if (!updatedRows) throw new Error('No feature returned from update');
        setFeatures((prev) =>
          prev.map((f) => (f.id === editing.id ? (updatedRows as Feature) : f))
        );
        setIsModalOpen(false);
        setEditing(null);
      } else {
        const payload = {
          title: formData.title.trim(),
          key: formData.key.trim(),
          description: formData.description?.trim() || null,
          active: formData.active,
          created_at: new Date().toISOString(),
        } as Partial<Feature> & { created_at: string };
        const { data: newRow, error: insertError } = await supabase
          .from('features')
          .insert(payload)
          .select('*')
          .maybeSingle();
        if (insertError) throw insertError;
        if (!newRow) throw new Error('No feature returned from insert');
        setFeatures((prev) => [newRow as Feature, ...prev]);
        setStats((prev) => {
          const nextTotal = (prev.total ?? 0) + 1;
          const nextActive = (prev.active ?? 0) + (newRow.active ? 1 : 0);
          return {
            total: nextTotal,
            active: nextActive,
            inactive: nextTotal - nextActive,
          };
        });
        setIsModalOpen(false);
      }
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : String(err ?? 'Failed to save')
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActiveInline = async (f: Feature) => {
    try {
      const { data, error } = await supabase
        .from('features')
        .update({ active: !f.active })
        .eq('id', f.id)
        .select('*')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('No feature returned from update');
      setFeatures((prev) =>
        prev.map((p) => (p.id === f.id ? (data as Feature) : p))
      );
      setStats((prev) => {
        const total = prev.total ?? features.length;
        const active =
          (prev.active ?? 0) + (data.active ? 1 : 0) - (f.active ? 1 : 0);
        return { total, active, inactive: total - active };
      });
    } catch (err) {
      // keep silent — show minimal UI feedback could be future work
      console.warn('Failed to update feature active status', err);
    }
  };

  const renderFeatureSection = () => {
    if (activeTab !== 'features') return null;
    if (loading) {
      return (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
            />
          ))}
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
          No features found.
        </div>
      );
    }
    return (
      <>
        <div className="hidden lg:block">
          <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
            <table className="min-w-full text-left text-sm text-gray-800">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleSort('title')}
                      className="flex items-center gap-2"
                    >
                      <span>Title</span>
                      <span className="text-xs text-gray-400">
                        {sort.key === 'title'
                          ? sort.dir === 'asc'
                            ? '�-�'
                            : sort.dir === 'desc'
                              ? '�-�'
                              : ''
                          : ''}
                      </span>
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleSort('key')}
                      className="flex items-center gap-2"
                    >
                      <span>Key</span>
                      <span className="text-xs text-gray-400">
                        {sort.key === 'key'
                          ? sort.dir === 'asc'
                            ? '�-�'
                            : sort.dir === 'desc'
                              ? '�-�'
                              : ''
                          : ''}
                      </span>
                    </button>
                  </th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleSort('active')}
                      className="flex items-center gap-2"
                    >
                      <span>Status</span>
                      <span className="text-xs text-gray-400">
                        {sort.key === 'active'
                          ? sort.dir === 'asc'
                            ? '�-�'
                            : sort.dir === 'desc'
                              ? '�-�'
                              : ''
                          : ''}
                      </span>
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleSort('created_at')}
                      className="flex items-center gap-2"
                    >
                      <span>Created</span>
                      <span className="text-xs text-gray-400">
                        {sort.key === 'created_at'
                          ? sort.dir === 'asc'
                            ? '�-�'
                            : sort.dir === 'desc'
                              ? '�-�'
                              : ''
                          : ''}
                      </span>
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr
                    key={f.id}
                    className="border-t border-gray-100 hover:bg-indigo-50"
                  >
                    <td className="px-4 py-4 align-top text-gray-900">
                      <div className="font-semibold">{f.title}</div>
                    </td>
                    <td className="px-4 py-4 align-top text-gray-900">
                      <div className="text-sm text-gray-700">{f.key}</div>
                    </td>
                    <td className="px-4 py-4 align-top text-gray-900">
                      <div className="max-w-lg text-sm text-gray-600">
                        {f.description ?? '�?"'}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top text-gray-900">
                      <label
                        htmlFor={`feature-active-${f.id}`}
                        className="inline-flex items-center gap-3"
                      >
                        <input
                          id={`feature-active-${f.id}`}
                          type="checkbox"
                          checked={f.active}
                          onChange={() => toggleActiveInline(f)}
                          className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-gray-600">
                          {f.active ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-4 align-top text-gray-900">
                      {formatDate(f.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex flex-wrap justify-end gap-3">
                        <ActionsMenu
                          id={f.id}
                          items={[
                            {
                              key: 'edit',
                              label: 'Edit',
                              onClick: () => handleEdit(f),
                              className: '!text-indigo-700',
                            },
                            {
                              key: 'delete',
                              label: 'Delete',
                              onClick: () => openDelete(f),
                              className: '!text-rose-700',
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="space-y-4 lg:hidden">
          {rows.map((f) => (
            <div
              key={f.id}
              className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold text-gray-900">
                  {f.title}
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={f.active}
                      onChange={() => toggleActiveInline(f)}
                      className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    {f.active ? 'Active' : 'Inactive'}
                  </label>
                  <ActionsMenu
                    id={f.id}
                    items={[
                      {
                        key: 'edit',
                        label: 'Edit',
                        onClick: () => handleEdit(f),
                        className: '!text-indigo-700',
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        onClick: () => openDelete(f),
                        className: '!text-rose-700',
                      },
                    ]}
                  />
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-600">{f.key}</div>
              <div className="mt-3 text-sm text-gray-600">
                {f.description ?? '�?"'}
              </div>
              <div className="mt-3 text-xs uppercase tracking-wide text-gray-500">
                Created {formatDate(f.created_at)}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Admin · Features
          </h1>
          <p className="text-lg text-gray-600">
            Review and manage application features available to badges and
            users.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">
                {typeof stats.total === 'number' ? stats.total : '--'}
              </span>{' '}
              total
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {typeof stats.active === 'number' ? stats.active : '--'}
              </span>{' '}
              active
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {typeof stats.inactive === 'number' ? stats.inactive : '--'}
              </span>{' '}
              inactive
            </div>
          </div>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="inline-flex gap-3 rounded-full bg-gray-100 p-1">
              {(
                [
                  { key: 'features', label: 'Features' },
                  { key: 'badge_features', label: 'Badge Features' },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    activeTab === tab.key
                      ? 'item-selected bg-white text-gray-900 shadow'
                      : 'bg-transparent text-gray-500 hover:bg-white hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div>
              <Button
                type="button"
                onClick={activeTab === 'features' ? openAdd : openAddMapping}
                className="item-selected inline-flex items-center rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {activeTab === 'features' ? 'Add Feature' : 'Add Mapping'}
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            <div className="mb-4 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <div className="w-full max-w-2xl flex-1">
                <label
                  htmlFor={
                    activeTab === 'features'
                      ? 'admin-feature-search'
                      : 'admin-badge-feature-search'
                  }
                  className="sr-only"
                >
                  {activeTab === 'features'
                    ? 'Search features'
                    : 'Search mappings'}
                </label>
                <div className="relative rounded-lg border border-gray-200 bg-white/60 px-3 py-2 shadow-sm">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      d="M21 21l-4.35-4.35"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="11"
                      cy="11"
                      r="6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {activeTab === 'features' ? (
                    <>
                      <input
                        id="admin-feature-search"
                        type="search"
                        value={featureSearch}
                        onChange={(e) => setFeatureSearch(e.target.value)}
                        placeholder="Search features by title, key or description"
                        className="w-full border-0 bg-transparent pl-9 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
                      />
                      {featureSearch && (
                        <button
                          onClick={() => setFeatureSearch('')}
                          type="button"
                          aria-label="Clear search"
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                        >
                          clear
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        id="admin-badge-feature-search"
                        type="search"
                        value={badgeFeatureSearch}
                        onChange={(e) => setBadgeFeatureSearch(e.target.value)}
                        placeholder="Search mappings by badge or feature"
                        className="w-full border-0 bg-transparent pl-9 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
                      />
                      {badgeFeatureSearch && (
                        <button
                          onClick={() => setBadgeFeatureSearch('')}
                          type="button"
                          aria-label="Clear search"
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                        >
                          clear
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {renderFeatureSection()}

            {activeTab === 'badge_features' && (
              <>
                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleBadgeSort('badge')}
                              className="flex items-center gap-2"
                            >
                              <span>Badge</span>
                              <span className="text-xs text-gray-400">
                                {badgeSort.key === 'badge'
                                  ? badgeSort.dir === 'asc'
                                    ? '�-�'
                                    : badgeSort.dir === 'desc'
                                      ? '�-�'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleBadgeSort('feature')}
                              className="flex items-center gap-2"
                            >
                              <span>Feature</span>
                              <span className="text-xs text-gray-400">
                                {badgeSort.key === 'feature'
                                  ? badgeSort.dir === 'asc'
                                    ? '�-�'
                                    : badgeSort.dir === 'desc'
                                      ? '�-�'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleBadgeSort('created_at')}
                              className="flex items-center gap-2"
                            >
                              <span>Created</span>
                              <span className="text-xs text-gray-400">
                                {badgeSort.key === 'created_at'
                                  ? badgeSort.dir === 'asc'
                                    ? '�-�'
                                    : badgeSort.dir === 'desc'
                                      ? '�-�'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {badgeRows.map((m) => (
                          <tr
                            key={`${m.badge_id}-${m.feature_id}`}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top text-gray-900">
                              {m.badge?.name ?? m.badge_id}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {m.feature?.title ?? m.feature_id}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {formatDate(m.created_at)}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex flex-wrap justify-end gap-3">
                                <ActionsMenu
                                  id={`${m.badge_id}-${m.feature_id}`}
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () => openEditMapping(m),
                                      className: '!text-indigo-700',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => handleDeleteMapping(m),
                                      className: '!text-rose-700',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="space-y-4 lg:hidden">
                  {badgeRows.map((m) => (
                    <div
                      key={`${m.badge_id}-${m.feature_id}`}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-lg font-semibold text-gray-900">
                          {m.badge?.name ?? m.badge_id}
                        </div>
                        <div className="flex items-center gap-3">
                          <ActionsMenu
                            id={`${m.badge_id}-${m.feature_id}`}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => openEditMapping(m),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => handleDeleteMapping(m),
                                className: '!text-rose-700',
                              },
                            ]}
                          />
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-gray-600">
                        Feature: {m.feature?.title ?? m.feature_id}
                      </div>
                      <div className="mt-3 text-xs uppercase tracking-wide text-gray-500">
                        Created {formatDate(m.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
                {!badgeFeatureHasData && (
                  <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                    No badge feature mappings found.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-xl flex-col rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close edit feature modal"
              disabled={saving}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {editing ? 'Edit Feature' : 'Add Feature'}
            </h2>
            <p className="text-sm text-gray-500">
              {editing ? (
                <>
                  Update feature{' '}
                  <span className="font-semibold text-gray-900">
                    {editing.title}
                  </span>
                </>
              ) : (
                <>Create a new feature the application can use.</>
              )}
            </p>
            {formError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formError}
              </div>
            )}
            <form
              className="mt-6 flex min-h-0 flex-1 flex-col"
              onSubmit={handleSave}
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
                <div>
                  <label
                    htmlFor="feature-title"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Title
                  </label>
                  <input
                    id="feature-title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="feature-key"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Key (technical identifier for the feature. Ex:
                    extra_background_videos)
                  </label>
                  <input
                    id="feature-key"
                    type="text"
                    value={formData.key}
                    onChange={(e) => handleInputChange('key', e.target.value)}
                    required
                    disabled={!!editing}
                    placeholder={editing ? 'Key cannot be changed' : ''}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="feature-description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Description
                  </label>
                  <textarea
                    id="feature-description"
                    value={formData.description ?? ''}
                    onChange={(e) =>
                      handleInputChange('description', e.target.value)
                    }
                    rows={3}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    id="feature-active"
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) =>
                      handleInputChange('active', e.target.checked)
                    }
                    className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor="feature-active"
                    className="text-sm text-gray-700"
                  >
                    Active
                  </label>
                </div>
              </div>
              <div className="flex flex-shrink-0 justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="item-selected rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isMappingModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeMappingModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close add mapping modal"
              disabled={mappingSaving}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {mappingEditing ? 'Edit Badge → Feature' : 'Add Badge → Feature'}
            </h2>
            <p className="text-sm text-gray-500">
              Create a mapping so a badge unlocks a feature.
            </p>
            {mappingFormError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {mappingFormError}
              </div>
            )}
            <form
              className="mt-6 flex min-h-0 flex-1 flex-col"
              onSubmit={handleSaveMapping}
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
                <div>
                  <label
                    htmlFor="mapping-badge"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Badge
                  </label>
                  <select
                    id="mapping-badge"
                    value={mappingFormData.badge_id}
                    onChange={(e) =>
                      handleMappingInputChange('badge_id', e.target.value)
                    }
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="" disabled>
                      Select badge
                    </option>
                    {badges.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="mapping-feature"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Feature
                  </label>
                  <select
                    id="mapping-feature"
                    value={mappingFormData.feature_id}
                    onChange={(e) =>
                      handleMappingInputChange('feature_id', e.target.value)
                    }
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="" disabled>
                      Select feature
                    </option>
                    {features.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.title} — {f.key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-shrink-0 justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  onClick={closeMappingModal}
                  disabled={mappingSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mappingSaving}
                  className="item-selected rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {mappingSaving
                    ? 'Saving...'
                    : mappingEditing
                      ? 'Save Changes'
                      : 'Create Mapping'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deleting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeDelete}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close delete feature modal"
              disabled={isDeleting}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Delete Feature</h2>
            <p className="text-sm text-gray-500">
              Are you sure you want to permanently delete the feature{' '}
              <span className="font-semibold text-gray-900">
                {deleting.title}
              </span>
              ? Badges linked to this feature may prevent deletion due to DB
              constraints. This action cannot be undone.
            </p>
            {deleteError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" onClick={closeDelete}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminFeatures;
